import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ContactCategoryDto,
  ContactFunctionDto,
  ContactMemberLinkDto,
  ContactMemberRelationLinkDto,
  ContactTitleDto,
  ContactTypeDto,
  DropdownContactTitleDTO,
  DropdownContactTypeDTO,
  LinkedMembersDTO,
} from '../interfaces';
import { sortBy } from 'underscore';
import { LinkedMembersParams } from 'src/app/shared/models/iContact/global/contacts-lien-membres';
import { environment } from 'src/environments/environment';
import { SearchContactFunctionsParams } from '../models/iContact/global/search-contact-functions';
import { SearchContactMembersRelationsLinksParams } from '../models/iContact/global/search-contact-members-relations-links';
import { SearchContactMembersLinksParams } from '../models/iContact/global/search-contact-members-links';

@Injectable({
  providedIn: 'root',
})
export class ContactsGlobalService {
  private iAdminURL: string;
  public apiContactiCourrierBaseUrl2: string;

  public stopRequestAutocompleteSender: Subject<void> = new Subject<void>();

  constructor(private http: HttpClient) {
    const config = environment;
    this.iAdminURL = config.iAdminApiBaseURL;
    this.apiContactiCourrierBaseUrl2 = config.apiContactiCourrierV2BaseUrl;
  }

  //#region contactTitres

  /**
   * A contact title may represent the official gender title or the employee official title
   * eg:
   * Miss, Mister, Director, Business Manager, etc. These are only available in french
   *
   * @returns {ContactTitleDto[]}
   */
  public fetchContactTitles(): Observable<ContactTitleDto[]> {
    return this.http.get<ContactTitleDto[]>(`${this.iAdminURL}/Global/contactTitres`);
  }

  public fetchMappedContactTitlesToPrimeNGDropdowns(): Observable<DropdownContactTitleDTO[]> {
    return this.fetchContactTitles().pipe(
      map((res: ContactTitleDto[]) => {
        let mappedContactTitleArrays: DropdownContactTitleDTO[] = [];

        res.forEach((contactTitle: ContactTitleDto) => {
          let mappedContactTitle: DropdownContactTitleDTO = {} as DropdownContactTitleDTO;
          mappedContactTitle = { ...contactTitle, value: contactTitle.titre, label: contactTitle.titre };
          mappedContactTitleArrays.push(mappedContactTitle);
        });

        mappedContactTitleArrays = sortBy(mappedContactTitleArrays, 'label');

        return mappedContactTitleArrays;
      })
    );
  }
  //#endregion

  public fetchContactFunctions(searchContactFunctionsParams?: SearchContactFunctionsParams): Observable<ContactFunctionDto[]> {
    return this.http.get<ContactFunctionDto[]>(`${this.iAdminURL}/Global/contactsFunctions`, {
      params: searchContactFunctionsParams ? { ...searchContactFunctionsParams } : {},
    });
  }

  public fetchContactCategories(): Observable<ContactCategoryDto[]> {
    return this.http.get<ContactCategoryDto[]>(`${this.iAdminURL}/Global/contactCategories`);
  }

  public fetchContactMembersRelationsLinks(
    searchContactMembersRelationsLinksParams?: SearchContactMembersRelationsLinksParams
  ): Observable<ContactMemberRelationLinkDto[]> {
    return this.http.get<ContactMemberRelationLinkDto[]>(`${this.iAdminURL}/Global/ContactsLiensMembresRelations`, {
      params: searchContactMembersRelationsLinksParams ? { ...searchContactMembersRelationsLinksParams } : {},
    });
  }

  //#region contactsLiensMembres
  public fetchContactMembersLinks(searchContactMembersRelationsLinksParams?: SearchContactMembersLinksParams): Observable<ContactMemberLinkDto[]> {
    return this.http.get<ContactMemberLinkDto[]>(`${this.iAdminURL}/Global/contactsLiensMembres`, {
      params: searchContactMembersRelationsLinksParams ? { ...searchContactMembersRelationsLinksParams } : {},
    });
  }

  //#endregion

  public fetchContactTypes(ids?: number[]): Observable<ContactTypeDto[]> {
    if (ids?.length) {
      return this.http.get<ContactTypeDto[]>(`${this.iAdminURL}/Global/contactTypes?ids=${ids}`);
    } else {
      return this.http.get<ContactTypeDto[]>(`${this.iAdminURL}/Global/contactTypes`);
    }
  }

  public fetchMappedContactTypesToPrimeNGDropdowns(): Observable<DropdownContactTypeDTO[]> {
    return this.fetchContactTypes().pipe(
      map((res: ContactTypeDto[]) => {
        let mappedContactTypesArray: DropdownContactTypeDTO[] = [];

        res.forEach((contactType: ContactTypeDto) => {
          let mappedContactType: DropdownContactTypeDTO = {} as DropdownContactTypeDTO;
          mappedContactType = { ...contactType, label: contactType.typeContact, value: contactType.id };
          mappedContactTypesArray.push(mappedContactType);
        });

        mappedContactTypesArray = sortBy(mappedContactTypesArray, 'label');

        return mappedContactTypesArray;
      })
    );
  }

  public enableMultiSociete(contactId: number): Observable<void> {
    return this.http.patch<void>(`${this.apiContactiCourrierBaseUrl2}/Global/contacts/${contactId}/SwitchToMultiSocietes`, {});
  }

  //#region LiensMembres
  public getSocietesV2(contactId: number): Observable<LinkedMembersDTO> {
    return this.http.get<LinkedMembersDTO>(`${this.apiContactiCourrierBaseUrl2}/Global/contacts/${contactId}/LiensMembres`);
  }

  public postSocietesV2(contactId: number, societeId: number, data: LinkedMembersParams): Observable<void> {
    return this.http.post<void>(`${this.apiContactiCourrierBaseUrl2}/Global/contacts/${contactId}/LiensMembres/${societeId}`, data);
  }

  public putSocietesV2(id: number, lienContactId: number, data: LinkedMembersParams): Observable<void> {
    return this.http.put<void>(`${this.apiContactiCourrierBaseUrl2}/Global/contacts/${id}/LiensMembres/${lienContactId}`, data);
  }
  public deleteSocietesV2(id: number, lienContactId: number, switchToUnique: boolean): Observable<void> {
    return this.http.delete<void>(
      `${this.apiContactiCourrierBaseUrl2}/Global/contacts/${id}/LiensMembres/${lienContactId}?switchToUnique=${switchToUnique}`
    );
  }

  public putSocieteUniqV2(id: number, societeId: number): Observable<void> {
    return this.http.put<void>(`${this.apiContactiCourrierBaseUrl2}/Global/contacts/${id}/linkToSociete?societeId=${societeId}`, {});
  }

  //#endregion
}
